import type { CSSProperties } from 'react';
import { useMemo } from 'react';
import { useViewportWidth } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import { defaultSettings, isInstagram } from '../../../constants';
import { useSpacingOffset } from '../../helpers/useSpacingOffset';

const DEFAULT_SPACING = 20;
const DEFAULT_BOTTOM_SPACING = 56;

type GetFloatingBottom = {
  hasBubbleText: boolean;
  verticalOffset: number;
  bottomSpacing: number;
};

const getFloatingBottom = ({
  hasBubbleText,
  verticalOffset,
  bottomSpacing
}: GetFloatingBottom) => {
  if (isInstagram) {
    return verticalOffset - DEFAULT_SPACING;
  }

  return hasBubbleText ? bottomSpacing - DEFAULT_SPACING : bottomSpacing;
};

type UseWindowPositionResult = {
  top: number | string;
  right: number | string;
  bottom: number | string;
  left: number | string;
  width: number;
  options?: CSSProperties;
};

export const useWindowPosition = (): UseWindowPositionResult => {
  const {
    align,
    position,
    bubbleText,
    windowWidth = defaultSettings.windowWidth
  } = useSettings();
  const { verticalOffset, horizontalOffset } = useSpacingOffset();

  const viewportWidth = useViewportWidth(true);
  const width = useMemo(() => {
    const intWindowWidth = parseInt(windowWidth as string, 10);
    const currentHorizontalOffset =
      position === 'floating' && align !== 'center'
        ? horizontalOffset + DEFAULT_SPACING
        : 2 * DEFAULT_SPACING;
    const shouldUseChatsSettingsWidth =
      viewportWidth - currentHorizontalOffset > intWindowWidth;
    const calculatedWidth =
      viewportWidth === 0
        ? intWindowWidth
        : viewportWidth - currentHorizontalOffset;

    return shouldUseChatsSettingsWidth ? intWindowWidth : calculatedWidth;
  }, [align, horizontalOffset, position, viewportWidth, windowWidth]);

  return useMemo(() => {
    const spacing: UseWindowPositionResult = {
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      width
    };
    const defaultFloatingBottomSpacing =
      verticalOffset + DEFAULT_BOTTOM_SPACING;

    if (position === 'floating') {
      if (align === 'left') {
        spacing.left = horizontalOffset;
        spacing.right = 'auto';
      }

      if (align === 'right') {
        spacing.right = horizontalOffset;
        spacing.left = 'auto';
      }

      spacing.bottom = getFloatingBottom({
        hasBubbleText: !!bubbleText,
        verticalOffset,
        bottomSpacing: defaultFloatingBottomSpacing
      });
      spacing.top = 'auto';
    }

    if (position === 'embed-bubble') {
      if (align === 'left') {
        spacing.right = 'auto';
      }

      if (align === 'right') {
        spacing.left = 'auto';
      }

      const verticalSpacing = bubbleText
        ? DEFAULT_BOTTOM_SPACING - DEFAULT_SPACING
        : DEFAULT_BOTTOM_SPACING;
      spacing.bottom = verticalSpacing;
      spacing.top = verticalSpacing;
    }

    if (['embed-bubble', 'floating'].includes(position) && align === 'center') {
      spacing.right = '50%';
      spacing.left = 'auto';
      spacing.options = {
        transform: 'translateX(50%)'
      };
    }

    return { ...spacing, width };
  }, [verticalOffset, position, align, width, bubbleText, horizontalOffset]);
};
